import { useMemo, useState } from "react";
import { BarChart2, Search } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Input } from "@/components/ui/input";
import { ReportCard } from "../components/ReportCard";
import { REPORTS, type ReportCategory } from "../types";

const CATEGORY_TABS: Array<{ value: "all" | ReportCategory; label: string }> = [
  { value: "all", label: "All" },
  { value: "projects", label: "Projects" },
  { value: "finance", label: "Finance" },
  { value: "people", label: "People" },
  { value: "work", label: "Work" },
];

export default function ReportsPage() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<"all" | ReportCategory>("all");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return REPORTS.filter((r) => {
      if (category !== "all" && r.category !== category) return false;
      if (!q) return true;
      return (
        r.title.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q)
      );
    });
  }, [search, category]);

  return (
    <AppLayout
      breadcrumb={[{ label: "PhazeX Hub", href: "/" }, { label: "Reports" }]}
    >
      <div className="w-full space-y-6">
        <div className="flex items-center gap-3">
          <BarChart2
            className="h-[18px] w-[18px] text-foreground"
            strokeWidth={1.5}
          />
          <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-[280px] shrink-0">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <Input
              placeholder="Search reports..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div
            className="flex items-center gap-1 rounded-md border bg-white p-1"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            {CATEGORY_TABS.map((t) => {
              const active = category === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setCategory(t.value)}
                  className="rounded px-3 py-1 text-[13px] transition-colors"
                  style={{
                    background: active ? "hsl(210,55%,92%)" : "transparent",
                    color: active ? "hsl(210,100%,28%)" : "hsl(215,16%,40%)",
                    fontWeight: active ? 500 : 400,
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div
            className="rounded-xl border bg-white py-16 text-center text-sm text-muted-foreground"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            No reports match the current filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((r) => (
              <ReportCard key={r.id} report={r} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
